import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography, useTheme } from
'@mui/material';
import {
  DatePicker,
  DateTimePicker,
  MobileDatePicker,
  MobileDateTimePicker,
  renderTimeViewClock } from
'@mui/x-date-pickers/';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// import moment from 'moment'
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { grey } from '@mui/material/colors';
import dayjs from 'dayjs';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import { formatCrashDateForNewIdQuery } from '../../utils/crash-payload-dates';
import {
  fromFormPickerChange,
  isAbsentFormDate,
  serializeApiDate,
  toFormPickerValue,
} from '../../utils/api-dates';
import { formatDateForList } from '../../utils';
import CameraBox from '../Camera';
import ErrorBoundary from '../ErrorBoundary';
import { NewApiBaseUrl } from '../../consts/new-api-base-url';
import {
  FORM_PICKER_FIELD_SX,
  getFormFieldsGridSx } from
'../../consts/form-fields-grid-sx';
import {
  filterOrphanSectionDividers,
  getCrashDetailElements,
  getFormFieldCellSx,
  getFormFieldLabelProps,
  getFormFieldLabelSx,
  getFormPickerSlotProps,
  getIdentificationPanelMobileSx,
  getIdentificationPanelSx,
  hasRenderableCrashDetailFields,
  isCrashIdentificationField } from
'../../consts/form-field-layout';
import FormSectionHeader from './form-section-header';
import FormSelectField from './form-select-field';
import { renderFormFieldLabel } from './form-field-label';

const Form = ({
  token,
  isMobile,
  submitRef,
  form,
  formModel,
  formList,
  formArray,
  handleCrashSubmit,
  isFilterForm,
  images,
  isReadOnly,
  isAdminModifyValidated,
  init,
  isPhotosModalOpen,
  onOpenPhotosModal,
  onClosePhotosModal
}) => {
  const canEditValidated = isReadOnly && isAdminModifyValidated;
  const fieldsReadOnly = isReadOnly && !isAdminModifyValidated;
  const { control, setValue, getValues, handleSubmit, watch, handlePicture } = useFormContext();
  const watchReportDate = watch('crash.Report date');
  const watchCrashDate = watch('crash.Crash date');
  const { _ } = useLingui();
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const isOnline = useOnlineStatus();
  const theme = useTheme();
  const isDark = theme.mode === 'dark';
  const formPath = formList[form.number] ?? form.name ?? 'crash';
  const fieldLabelProps = getFormFieldLabelProps(isMobile);
  const pickerSlotProps = getFormPickerSlotProps(isMobile);
  const fieldLabelSx = getFormFieldLabelSx(isMobile);

  const renderFieldLabel = (elem) => renderFormFieldLabel(elem, isMobile, theme);

  const baseFieldSx = (theme, isDark) => ({
    backgroundColor: isDark ? theme.palette.newColors.lightGray : "#f5f5f5",
    color: isDark ? theme.palette.newColors.gray : "#3C3C3B",
    borderRadius: 1,
    "& label": {
      color: theme.palette.newColors.gray
    },

    "& .MuiSelect-select": {
      color: isDark ? theme.palette.newColors.gray : "#3C3C3B"
    },
    '& .MuiOutlinedInput-root': {
      color: theme.palette.newColors.gray
    },
    "& .MuiSelect-icon": {
      color: theme.palette.newColors.gray
    },
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" }
  });

  /**
   * Elementi campi per sezione: formModel (stato Home), altrimenti init.configuration (offline / primo paint).
   * Il render usava `formModel[form.type].elements` senza fallback: con formModel vuoto o non ancora popolato non usciva nessun campo.
   * Chiavi sezione allineate in modo case-insensitive tra formModel e init.
   */
  const getSectionElements = (sectionType) => {
    const lower = String(sectionType ?? '').toLowerCase();
    if (!lower) return [];
    const keys = new Set([
    ...Object.keys(formModel || {}),
    ...Object.keys(init?.configuration || {})]
    );
    for (const k of keys) {
      if (k.toLowerCase() !== lower) continue;
      const fromModel = formModel?.[k]?.elements;
      if (Array.isArray(fromModel) && fromModel.length > 0) return fromModel;
      const fromInit = init?.configuration?.[k]?.elements;
      if (Array.isArray(fromInit) && fromInit.length > 0) return fromInit;
    }
    return [];
  };

  const sectionElements = useMemo(
    () => getSectionElements(form.type),
    [form.type, formModel, init],
  );

  const renderableFieldElements = useMemo(() => {
    const sourceElements = form.type === 'crash'
      ? getCrashDetailElements(sectionElements)
      : sectionElements;
    return filterOrphanSectionDividers(sourceElements);
  }, [form.type, sectionElements]);

  const showCrashDetailsHeader = form.type === 'crash'
    && !isMobile
    && hasRenderableCrashDetailFields(sectionElements);


  const getNewId = useCallback((date) => {
    if (!isOnline) {
      setValue('crash.Crash id', 'Not Avail. offline');
      return;
    }
    const crashDateIso = formatCrashDateForNewIdQuery(date);
    // eslint-disable-next-line no-restricted-globals
    const newCrashIdUrl = new URL(`${NewApiBaseUrl}/Crash/new_crash_id`);
    newCrashIdUrl.searchParams.set('crash_date', crashDateIso);

    fetch(newCrashIdUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).
    then((res) => res.json()).
    then((res) => {
      // The field name is 'crash.Crash id' as defined in the Controller
      const crashId = res.responseData?.crash_id || res.crash_id || res.data?.crash_id;
      if (crashId) {
        setValue('crash.Crash id', crashId);
      } else {

      }
    }).
    catch((error) => {

    });
  }, [token, setValue, isOnline]);

  useEffect(() => {
    if (isFilterForm || form.type !== 'crash') return;
    if (isAbsentFormDate(watchReportDate)) setValue('crash.Report date', serializeApiDate(dayjs()));
    if (isAbsentFormDate(watchCrashDate)) setValue('crash.Crash date', serializeApiDate(dayjs()));
  }, [watchReportDate, watchCrashDate, isFilterForm, form.type, setValue]);

  useEffect(() => {
    if (isFilterForm || form.type !== 'crash') return;

    const crashDateForId = watchCrashDate || dayjs();
    const crashId = getValues('crash.Crash id');
    const isModifying = getValues('isModifying') === true;
    const isNew = getValues('isNew') === true;

    if (!crashId) {
      getNewId(crashDateForId);
      setValue('isNew', true);
      return;
    }

    if (isNew && !isModifying) {
      getNewId(crashDateForId);
      return;
    }

    if (!isNew) {
      setValue('isModifying', true);
      setValue('isNew', false);
    }
  }, [watchCrashDate, isFilterForm, form.type, setValue, getValues, getNewId]);

  // Handle file input change
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // const imageUrl = URL.createObjectURL(file)
      const filereader = new FileReader();
      filereader.readAsDataURL(file);
      filereader.onload = (evt) => {
        const base64 = evt.target.result;
        handlePicture(base64);
      };
    }
  };

  const handleCameraClose = () => {
    setIsCameraOpen(false);
  };

  const handleDownload = (base64Data, crashId, index) => {
    const link = document.createElement('a');
    const ext = base64Data.match(/^data:image\/(.*);base64,/)[1];
    link.href = base64Data;
    link.download = `${crashId}_${index + 1}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      name={form.type}
      component="form"
      ref={submitRef}
      onSubmit={handleSubmit(handleCrashSubmit)}
      display='flex'
      flexDirection='column'
      pl={isMobile ? 1.25 : 0.75}
      pr={isMobile ? 1.25 : 0.75}
      noValidate
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& > *': {
          overflow: 'visible'
        }
      }}>
      
      {/* Scrollable form fields section */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          pt: isMobile ? 1 : 0.75,
          pb: isMobile ? 1 : 0.25,
          pr: 0.5,
          '& .MuiInputLabel-root': fieldLabelSx,
          '& .MuiPickersTextField-root': { width: '100%', overflow: 'visible' },
          '& .MuiAutocomplete-root': { overflow: 'visible' },
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
          }
        }}>
        
      {form.type === 'crash' &&
        <Box sx={{ flexShrink: 0, width: '100%' }}>
          <FormSectionHeader title={<Trans>Identification</Trans>} compact isMobile={isMobile} />
          <Box sx={isMobile ? getIdentificationPanelMobileSx() : getIdentificationPanelSx(theme, isDark)}>
            {sectionElements.
              filter((element) => isCrashIdentificationField(element)).
              map((elem) => {
                if (elem.label === 'Report date') {
                  return (
                    <FormControl key={`crash-${elem.type}-${elem.label}`}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                          name={'crash.Report date'}
                          control={control}
                          render={({ field: { value, onChange } }) =>
                            isMobile ?
                              <MobileDatePicker
                                label={renderFieldLabel(elem)}
                                disableFuture
                                readOnly={fieldsReadOnly}
                                value={toFormPickerValue(value)}
                                onChange={fromFormPickerChange(onChange)}
                                onAccept={fromFormPickerChange(onChange)}
                                slotProps={pickerSlotProps}
                                sx={FORM_PICKER_FIELD_SX}
                              /> :
                              <DatePicker
                                label={renderFieldLabel(elem)}
                                disableFuture
                                readOnly={fieldsReadOnly}
                                value={toFormPickerValue(value)}
                                onChange={fromFormPickerChange(onChange)}
                                onAccept={fromFormPickerChange(onChange)}
                                slotProps={pickerSlotProps}
                                sx={FORM_PICKER_FIELD_SX}
                              />
                          }
                        />
                      </LocalizationProvider>
                    </FormControl>
                  );
                }

                if (elem.label === 'Crash id') {
                  const crashDateLabel = watchCrashDate ?
                    formatDateForList(watchCrashDate) :
                    '';
                  const crashIdTooltip = crashDateLabel ?
                    `${_(msg`Crash date`)}: ${crashDateLabel}. ${_(msg`The ID uses the UTC day of the crash date.`)}` :
                    _(msg`The ID uses the UTC day of the crash date.`);
                  return (
                    <FormControl key={`crash-${elem.type}-${elem.label}`}>
                      <Controller
                        name={'crash.Crash id'}
                        control={control}
                        render={({ field }) =>
                          <Tooltip title={crashIdTooltip} placement="top" arrow>
                            <TextField
                              fullWidth
                              margin="none"
                              required={elem.isRequired}
                              id={elem.label}
                              label={renderFieldLabel(elem)}
                              value={field.value}
                              helperText={crashDateLabel ? `${_(msg`Crash date`)}: ${crashDateLabel}` : undefined}
                              InputProps={{ readOnly: true }}
                              InputLabelProps={fieldLabelProps}
                              {...field}
                            />
                          </Tooltip>
                        }
                      />
                    </FormControl>
                  );
                }

                if (elem.label === 'Crash date') {
                  return (
                    <FormControl key={`crash-${elem.type}-${elem.label}`}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Controller
                          name={`crash.${elem.label}`}
                          control={control}
                          render={({ field: { value, onChange } }) =>
                            isMobile ?
                              <MobileDateTimePicker
                                label={renderFieldLabel(elem)}
                                disableFuture
                                readOnly={fieldsReadOnly}
                                value={toFormPickerValue(value)}
                                onChange={fromFormPickerChange(onChange)}
                                onAccept={fromFormPickerChange(onChange)}
                                slotProps={pickerSlotProps}
                                sx={FORM_PICKER_FIELD_SX}
                              /> :
                              <DateTimePicker
                                label={renderFieldLabel(elem)}
                                disableFuture
                                readOnly={fieldsReadOnly}
                                value={toFormPickerValue(value)}
                                onChange={fromFormPickerChange(onChange)}
                                onAccept={fromFormPickerChange(onChange)}
                                viewRenderers={{
                                  hours: renderTimeViewClock,
                                  minutes: renderTimeViewClock,
                                  seconds: renderTimeViewClock,
                                }}
                                slotProps={pickerSlotProps}
                                sx={FORM_PICKER_FIELD_SX}
                              />
                          }
                        />
                      </LocalizationProvider>
                    </FormControl>
                  );
                }

                if (elem.type === 'coordinates') {
                  return (
                    <FormControl key={`crash-${elem.type}-${elem.label}`}>
                      <Controller
                        name={`crash.${elem.label}`}
                        control={control}
                        render={({ field }) =>
                          <TextField
                            margin="none"
                            fullWidth
                            required={elem.isRequired}
                            id={elem.label}
                            label={renderFieldLabel(elem)}
                            value={field.value}
                            InputProps={{ readOnly: true }}
                            InputLabelProps={fieldLabelProps}
                            {...field}
                          />
                        }
                      />
                    </FormControl>
                  );
                }

                return null;
              })}
          </Box>
        </Box>
      }

      {showCrashDetailsHeader &&
        <FormSectionHeader title={<Trans>Details</Trans>} isMobile={isMobile} />
      }

      {/* <MemoDynForm
           isMobile={isMobile}
           formModel={formModel}
           formList={formList}
           form={form}
           formArray={formArray}
           isReadOnly={fieldsReadOnly}
          /> */}

      <Box
        sx={{
          ...getFormFieldsGridSx(isMobile),
          flexShrink: 0,
          width: '100%',
          '& > .MuiFormControl-root': { overflow: 'visible' },
        }}>
      {renderableFieldElements.map((elem) => {
          const cellSx = getFormFieldCellSx(isMobile, elem);

          if (elem.type === 'divider') {
            return (
              <FormSectionHeader
                key={`${formPath}-${elem.type}-${elem.label || elem.position}`}
                title={elem.label || ''}
                isMobile={isMobile}
              />
            );
          }

          if (elem.type === 'datetime') {
            return (
              <FormControl key={`${formPath}-${elem.type}-${elem.label}`} sx={cellSx}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name={`${formPath}.${elem.label}`}
                    control={control}
                    render={({ field: { value, onChange } }) =>
                    isMobile ?
                    <MobileDateTimePicker
                      label={renderFieldLabel(elem)}
                      disableFuture
                      readOnly={fieldsReadOnly}
                      value={toFormPickerValue(value)}
                      onChange={fromFormPickerChange(onChange)}
                      onAccept={fromFormPickerChange(onChange)}
                      slotProps={pickerSlotProps}
                      sx={FORM_PICKER_FIELD_SX} /> :

                    <DateTimePicker
                      label={renderFieldLabel(elem)}
                      disableFuture
                      readOnly={fieldsReadOnly}
                      value={toFormPickerValue(value)}
                      onChange={fromFormPickerChange(onChange)}
                      onAccept={fromFormPickerChange(onChange)}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock
                      }}
                      slotProps={pickerSlotProps}
                      sx={FORM_PICKER_FIELD_SX} />


                    } />
                  
                </LocalizationProvider>
              </FormControl>);

          }

          if (elem.type === 'date') {

            return (
              <FormControl key={`${formPath}-${elem.type}-${elem.label}`} sx={cellSx}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name={`${formPath}.${elem.label}`}
                    control={control}
                    render={({ field: { value, onChange } }) =>
                    isMobile ?
                    <MobileDatePicker
                      label={renderFieldLabel(elem)}
                      disableFuture
                      readOnly={fieldsReadOnly}
                      value={toFormPickerValue(value)}
                      onChange={fromFormPickerChange(onChange)}
                      onAccept={fromFormPickerChange(onChange)}
                      slotProps={pickerSlotProps}
                      sx={FORM_PICKER_FIELD_SX} /> :

                    <DatePicker
                      label={renderFieldLabel(elem)}
                      disableFuture
                      readOnly={fieldsReadOnly}
                      value={toFormPickerValue(value)}
                      onChange={fromFormPickerChange(onChange)}
                      onAccept={fromFormPickerChange(onChange)}
                      slotProps={pickerSlotProps}
                      sx={FORM_PICKER_FIELD_SX} />

                    } />
                  
                </LocalizationProvider>
              </FormControl>);

          }

          if (elem.type === 'select') {
            return (
              <FormSelectField
                key={`${formPath}-${elem.type}-${elem.label}`}
                name={`${formPath}.${elem.label}`}
                control={control}
                elem={elem}
                formPath={formPath}
                fieldsReadOnly={fieldsReadOnly}
                theme={theme}
                cellSx={cellSx}
                labelProps={fieldLabelProps}
                isMobile={isMobile}
              />);


          }

          return (
            <FormControl key={`${formPath}-${elem.type}-${elem.label}`} sx={cellSx}>
              <Controller
                name={`${formPath}.${elem.label}`}
                control={control}
                render={({ field }) =>
                <TextField
                  {...field}
                  margin="none"
                  fullWidth
                  required={elem.isRequired}
                  id={elem.label}
                  label={renderFieldLabel(elem)}
                  value={field.value}
                  InputProps={{ readOnly: fieldsReadOnly }}
                  InputLabelProps={fieldLabelProps}
                  onChange={field.onChange}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position='start'>{elem.unit}&nbsp;</InputAdornment>
                    }
                  }} />

                } />
              
            </FormControl>);

        })}
      </Box>
      </Box>

      {/* Fixed bottom section: Camera (badge), Upload/Add, Take Picture */}
      {form.type === 'crash' &&
      <Box
        sx={{
          flexShrink: 0
        }}>
        
          <Stack direction='row' spacing={1} justifyContent={isMobile ? 'space-between' : 'center'} alignItems='center'
        sx={{ mt: 1.5 }}>
            {/* Camera button: when validated, show only if admin-modify flow (can edit) or there are photos to view 
            {(!isReadOnly || !!images?.length || canEditValidated) && (
             <Badge
               badgeContent={images?.length ? images.length : '+'}
               sx={{
                 '& .MuiBadge-badge': {
                   backgroundColor: theme.palette.newColors.gray,
                   color: theme.palette.newColors.lightGray,
                   fontSize: images?.length ? undefined : '0.95rem',
                   fontWeight: images?.length ? undefined : 500,
                 },
               }}
             >
               <span>
                 <Button
                   type="button"
                   aria-label={_(msg`view photos`)}
                   onClick={onOpenPhotosModal}
                   sx={{
                     background: theme.palette.newColors.gray,
                     '&:hover': { background: theme.palette.newColors.gray },
                     borderRadius: '50%',
                     width: 36,
                     height: 36,
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     minWidth: 0,
                     p: 0,
                   }}
                 >
                   <PhotoCamera sx={{ color: theme.palette.newColors.lightGray }} />
                 </Button>
               </span>
             </Badge>
            )}
             */}
          </Stack>
          {isCameraOpen &&
        <ErrorBoundary>
              <CameraBox
            handlePicture={handlePicture}
            handleCameraClose={handleCameraClose} />
          
            </ErrorBoundary>
        }
          {/* Photos modal – list with download and delete */}
          <Dialog
          open={isPhotosModalOpen}
          onClose={(_, reason) => {if (reason !== 'backdropClick') onClosePhotosModal();}}
          fullScreen={isMobile}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { minHeight: '70vh', maxWidth: 520 } }}>
          
            <DialogTitle
            sx={{
              color: theme.palette.newColors.gray,
              fontWeight: 600,
              textAlign: 'center',
              textTransform: 'uppercase',
              position: 'relative'
            }}>
            
              <Trans>Photos</Trans> {images?.length && `(${images.length})`}
              <IconButton
              aria-label={_(msg`close`)}
              onClick={onClosePhotosModal}
              sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
              
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0, '&:last-of-type': { pb: 2 } }} onClick={(e) => e.stopPropagation()}>
              {(!isReadOnly || canEditValidated) &&
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', pt: 2, pb: 2, px: 1 }}>
                  <Button
                type="button"
                sx={{ background: theme.palette.newColors.cyanGradient, textTransform: 'none' }}
                variant='contained'
                component="label"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<CollectionsIcon />}>
                
                    <Trans>Add file or picture</Trans>
                    <input
                  type='file'
                  accept='image/*'
                  hidden
                  multiple
                  onChange={(event) => {
                    const files = event.target.files;
                    if (files?.length) {
                      [...files].forEach((file) => {
                        const filereader = new FileReader();
                        filereader.readAsDataURL(file);
                        filereader.onload = (evt) => {
                          const base64 = evt.target.result;
                          handlePicture(base64);
                        };
                      });
                    }
                    event.target.value = '';
                  }} />
                
                  </Button>
                  {/*
                {isMobile && (
                 <Button
                   type="button"
                   variant='contained'
                   size='medium'
                   startIcon={<PhotoCamera />}
                   onClick={() => setIsCameraOpen(true)}
                 >
                   <Trans>Take Picture</Trans>
                 </Button>
                )}
                 */}
                </Box>
            }
              {!images?.length ?
            <Typography color="text.secondary" sx={{ py: 2 }}><Trans>No photos</Trans></Typography> :

            <Box
              sx={{
                width: '100%',
                maxHeight: '60vh',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                py: 2,
                px: 1
              }}>
              
                  {images.map((image, index) =>
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  flexShrink: 0,
                  width: '92%',
                  maxWidth: 480,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  p: 1
                }}>
                
                      <img
                  srcSet={image}
                  src={image}
                  alt=""
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: 'none',
                    verticalAlign: 'middle'
                  }} />
                
                      <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 0.5
                  }}>
                  
                        <IconButton
                    size="small"
                    sx={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                    aria-label={_(msg`download picture no ${index}`)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(image, getValues('crash.Crash id'), index);
                    }}>
                    
                          <DownloadRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                    size="small"
                    disabled={isReadOnly && !canEditValidated}
                    sx={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                    aria-label={_(msg`delete picture no ${index}`)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePicture(null, index);
                    }}>
                    
                          <DeleteIcon fontSize="small" color={isReadOnly && !canEditValidated ? 'disabled' : 'error'} />
                        </IconButton>
                      </Box>
                    </Box>
              )}
                </Box>
            }
            </DialogContent>
          </Dialog>
        </Box>
      }
    </Box>);

};

export default Form;

// import CameraAltIcon from '@mui/icons-material/CameraAlt';
// import ReactWebcam from 'react-webcam';

// const CameraUpload = () => {
//   const { control, handleSubmit, reset } = useForm();
//   const [selectedImage, setSelectedImage] = useState(null);
//   const [isWebcamOpen, setIsWebcamOpen] = useState(false);
//   const webcamRef = useRef(null);

//   // Handle file input change
//   const handleFileChange = (event) => {
//     const file = event.target.files[0];
//     if (file) {
//       const imageUrl = URL.createObjectURL(file);
//       setSelectedImage(imageUrl);
//     }
//   };

//   // Capture photo from webcam
//   const capturePhoto = () => {
//     const imageSrc = webcamRef.current.getScreenshot();
//     setSelectedImage(imageSrc);
//     setIsWebcamOpen(false);
//   };

//   return (
//     <form onSubmit={handleSubmit(onSubmit)}>
//       <Stack direction="column" spacing={3} alignItems="center">
//         {/* Webcam Toggle Button */}
//         <Button
//           variant="contained"
//           onClick={() => setIsWebcamOpen(!isWebcamOpen)}
//           startIcon={<CameraAltIcon />}
//         >
//           {isWebcamOpen ? 'Close Camera' : 'Open Camera'}
//         </Button>

//         {/* Webcam Capture */}
//         {isWebcamOpen && (
//           <Box sx={{ marginBottom: 2 }}>
//             <ReactWebcam
//               audio={false}
//               ref={webcamRef}
//               screenshotFormat="image/jpeg"
//               width={300}
//               videoConstraints={{ facingMode: 'environment' }}
//             />
//             <Button
//               variant="contained"
//               color="primary"
//               onClick={capturePhoto}
//               sx={{ marginTop: 2 }}
//             >
//               Capture Photo
//             </Button>
//           </Box>
//         )}

//         <Divider sx={{ width: '100%', marginY: 2 }} />

//         {/* File Upload Button */}
//         <Controller
//           name="image"
//           control={control}
//           render={({ field }) => (
//             <Button
//               variant="contained"
//               component="label"
//               startIcon={<PhotoCamera />}
//             >
//               Upload from Device
//               <input
//                 {...field}
//                 type="file"
//                 accept="image/*"
//                 hidden
//                 onChange={(event) => {
//                   handleFileChange(event);
//                   field.onChange(event.target.files);
//                 }}
//               />
//             </Button>
//           )}
//         />

//         {/* Optional Icon Button */}
//         <Controller
//           name="imageIconUpload"
//           control={control}
//           render={({ field }) => (
//             <IconButton
//               color="primary"
//               component="label"
//               aria-label="upload picture"
//             >
//               <UploadFileIcon />
//               <input
//                 {...field}
//                 type="file"
//                 accept="image/*"
//                 hidden
//                 onChange={(event) => {
//                   handleFileChange(event);
//                   field.onChange(event.target.files);
//                 }}
//               />
//             </IconButton>
//           )}
//         />

//         {/* Submit Button */}
//         <Button type="submit" variant="contained" color="success">
//           Submit
//         </Button>

//         {/* Reset Button */}
//         <Button
//           variant="outlined"
//           color="secondary"
//           onClick={() => {
//             reset();
//             setSelectedImage(null);
//           }}
//         >
//           Reset
//         </Button>

//         {/* Display the selected image */}
//         {selectedImage && (
//           <Box mt={3}>
//             <Typography variant="h6">Selected Image:</Typography>
//             <img
//               src={selectedImage}
//               alt="Selected"
//               style={{ width: '100%', maxWidth: '300px', borderRadius: '8px' }}
//             />
//           </Box>
//         )}
//       </Stack>
//     </form>
//   );
// };

// export default CameraUpload;